// `AbonnementSection` — Settings → Abonnement (EPIC-15 US-189..US-200).
//
// Mounts inside the Wave 5a.3 Settings shell (see
// `apps/web/src/features/settings/sections/Abonnement.tsx`). Renders:
//
//   - Active-plan summary card (US-189) — including "Annulation prévue le X"
//     pill when the subscription is canceled at period-end.
//   - Settings-internal trial banner (US-190).
//   - Monthly/Yearly cycle toggle (US-192/193) — drives PlanGrid + Checkout
//     only when the server catalog exposes an active yearly Stripe Price.
//   - 4-tier plan grid (US-192) — clicking a non-active card opens the
//     `<UpgradeConfirm />` or `<DowngradeConfirm />` modal (US-193/4).
//   - Payment method row + Stripe Elements modal (US-197).
//   - Invoices list (US-198).
//   - Cancel / Cancel-trial CTAs (US-195/6) — proper Dialogs, no
//     `window.confirm`. Cancel-paid uses a retention prompt with feedback +
//     immediate-vs-EoP toggle.
//   - Soft-block banner when subscription is expired (US-200).

import { useEffect, useState } from 'react';

import {
  type BillingCycle,
  type BillingPlan,
  type BillingPrice,
  type BillingResponse,
  useCancelSubscription,
  useCancelTrial,
  useLinkedinAccounts,
  useStartCheckout,
  useStartBillingPortal,
  useBillingPrices,
  useUpgradeSubscription,
} from '@barel/api-client/react';
import { useLocale, useT } from '@barel/i18n';
import {
  Banner,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Pill,
  Radio,
  RadioGroup,
  Spinner,
  Textarea,
  toast,
} from '@barel/ui';

import { formatDate } from '@/lib/format';

import { DowngradeConfirm } from './DowngradeConfirm';
import { InvoicesList } from './InvoicesList';
import { PaymentMethodModal, PaymentMethodRow } from './PaymentMethod';
import { QuotaPill } from './QuotaGate';
import { PlanGrid } from './PlanGrid';
import { SettingsTrialBanner } from './TrialBanner';
import { TrialExpiredOverlay } from './TrialExpiredOverlay';
import { UpgradeConfirm } from './UpgradeConfirm';
import { useSubscription } from './useSubscription';
import { findBillingPrice, formatBillingPrice, isPlanUpgrade } from './prices';

export function AbonnementSection() {
  const t = useT('billing');
  const locale = useLocale();
  const { data, isLoading } = useSubscription();
  const pricesQuery = useBillingPrices();
  const prices = pricesQuery.data?.prices ?? [];
  const checkout = useStartCheckout();
  const portal = useStartBillingPortal();
  const upgrade = useUpgradeSubscription();
  const cancel = useCancelSubscription();
  const cancelTrial = useCancelTrial();
  const accountsQuery = useLinkedinAccounts();

  const [pendingPlan, setPendingPlan] = useState<BillingPlan | null>(null);
  const [pendingFlow, setPendingFlow] = useState<'upgrade' | 'downgrade' | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTrialOpen, setCancelTrialOpen] = useState(false);
  const [cancelTiming, setCancelTiming] = useState<'eop' | 'now'>('eop');
  const [cancelFeedback, setCancelFeedback] = useState<string>('');

  // `availableCycles` is the authoritative server capability. Deriving from
  // active rows keeps a rolling deployment compatible with an older API that
  // returned only `prices`, without ever inventing yearly availability.
  const yearlyAvailable =
    pricesQuery.data?.availableCycles?.includes('yearly') ??
    prices.some((price) => price.cycle === 'yearly' && price.active);
  const selectedCycle: BillingCycle =
    billingCycle === 'yearly' && yearlyAvailable ? 'yearly' : 'monthly';

  // A catalog refresh can withdraw yearly prices after the user selected
  // them. Clear both the stale preference and any quote opened for that
  // cycle, so later catalog recovery cannot silently switch the UI back to
  // yearly or submit a different cycle than the confirmation first showed.
  useEffect(() => {
    if (!pricesQuery.isSuccess || billingCycle !== 'yearly' || yearlyAvailable) return;
    setBillingCycle('monthly');
    setPendingPlan(null);
    setPendingFlow(null);
  }, [billingCycle, pricesQuery.isSuccess, yearlyAvailable]);

  const sub = data?.subscription;
  const trial = data?.trial;
  const activePlan: BillingPlan = sub?.plan ?? 'discover';

  const onPlanSelect = (plan: BillingPlan) => {
    if (plan === activePlan) return;
    if (!findBillingPrice(prices, plan, selectedCycle)) return;
    if (!sub || !sub.stripeSubscriptionId) {
      // No live sub → start a Checkout flow.
      if (plan === 'discover') return;
      checkout.mutate(
        { plan: plan as 'pro' | 'scale' | 'agency', cycle: selectedCycle, trialDays: 7 },
        {
          onSuccess: (res) => {
            if (res.url) window.location.href = res.url;
          },
          onError: (err) => toast({ tone: 'error', title: err.message }),
        },
      );
      return;
    }
    setPendingPlan(plan);
    setPendingFlow(isPlanUpgrade(activePlan, plan) ? 'upgrade' : 'downgrade');
  };

  const onConfirmChange = () => {
    if (!pendingPlan) return;
    upgrade.mutate(
      { plan: pendingPlan, cycle: selectedCycle },
      {
        onSuccess: () => {
          toast({
            tone: 'success',
            title:
              pendingFlow === 'upgrade' ? t('upgrade.successToast') : t('downgrade.successToast'),
          });
          setPendingPlan(null);
          setPendingFlow(null);
        },
        onError: (err) => {
          toast({ tone: 'error', title: err.message });
        },
      },
    );
  };

  const onConfirmCancel = () => {
    const atPeriodEnd = cancelTiming === 'eop';
    cancel.mutate(
      { atPeriodEnd, reason: cancelFeedback || undefined },
      {
        onSuccess: () => {
          toast({
            tone: 'success',
            title: atPeriodEnd
              ? t('cancel.successToastEop', {
                  date: sub?.currentPeriodEnd ? formatDate(Number(sub.currentPeriodEnd)) : '',
                })
              : t('cancel.successToastNow'),
          });
          setCancelOpen(false);
          setCancelFeedback('');
          setCancelTiming('eop');
        },
        onError: (err) => {
          toast({ tone: 'error', title: err.message });
        },
      },
    );
  };

  const onConfirmCancelTrial = () => {
    cancelTrial.mutate(undefined, {
      onSuccess: () => {
        toast({ tone: 'success', title: t('cancelTrial.successToast') });
        setCancelTrialOpen(false);
      },
      onError: (err) => {
        toast({ tone: 'error', title: err.message });
      },
    });
  };

  if (isLoading) {
    return (
      <section data-section="billing" className="flex min-h-[200px] items-center justify-center">
        <Spinner size="md" />
      </section>
    );
  }

  const periodEndDate = sub?.currentPeriodEnd ? formatDate(Number(sub.currentPeriodEnd)) : '';
  const trialEndDate = sub?.trialEndsAt ? formatDate(Number(sub.trialEndsAt)) : '';

  return (
    <section
      data-section="billing"
      aria-labelledby="settings-billing-title"
      className="flex flex-col gap-6"
    >
      <header className="flex flex-col gap-1 border-b border-default pb-4">
        <h1 id="settings-billing-title" className="text-lg font-semibold text-primary">
          {t('page.title')}
        </h1>
        <p className="text-xs text-tertiary">{t('page.subtitle')}</p>
      </header>

      <SettingsTrialBanner
        data={data}
        onCancelTrial={() => setCancelTrialOpen(true)}
        onUpgrade={() => onPlanSelect('pro')}
      />

      {trial?.expired ? (
        <Banner
          tone="error"
          title={t('expired.softTitle')}
          data-testid="abonnement-expired-banner"
          action={
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                portal.mutate(undefined, {
                  onSuccess: (res) => {
                    if (res.url) window.location.href = res.url;
                  },
                })
              }
            >
              {t('expired.softCta')}
            </Button>
          }
        >
          {t('expired.softBody')}
        </Banner>
      ) : null}

      <PlanSummary data={data} prices={prices} locale={locale} />

      {pricesQuery.isError ? (
        <Banner
          tone="warning"
          title={t('prices.error')}
          action={
            <Button type="button" variant="ghost" size="sm" onClick={() => pricesQuery.refetch()}>
              {t('prices.retry')}
            </Button>
          }
          data-testid="billing-prices-error"
        >
          {t('prices.errorBody')}
        </Banner>
      ) : null}

      {pricesQuery.isSuccess ? (
        yearlyAvailable ? (
          <BillingCycleToggle value={selectedCycle} onChange={setBillingCycle} />
        ) : (
          <p className="text-center text-xs text-tertiary" data-testid="cycle-yearly-unavailable">
            {t('cycle.yearlyUnavailable')}
          </p>
        )
      ) : null}

      <PlanGrid
        activePlan={activePlan}
        prices={prices}
        cycle={selectedCycle}
        onSelect={onPlanSelect}
        disabled={pricesQuery.isLoading || upgrade.isPending || checkout.isPending}
      />

      <PaymentMethodRow
        brand={sub?.paymentMethodBrand ?? null}
        last4={sub?.paymentMethodLast4 ?? null}
        onEdit={() => setPaymentOpen(true)}
      />
      <PaymentMethodModal open={paymentOpen} onClose={() => setPaymentOpen(false)} />

      <div className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold text-primary">{t('invoices.title')}</h2>
        </div>
        <InvoicesList />
      </div>

      {sub?.stripeSubscriptionId && sub.status !== 'canceled' && !sub.cancelAtPeriodEnd ? (
        <div className="pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCancelOpen(true)}
            data-testid="cancel-subscription"
          >
            {t('cancel.title')}
          </Button>
        </div>
      ) : null}

      <UpgradeConfirm
        open={pendingFlow === 'upgrade'}
        targetPlan={pendingPlan}
        price={pendingPlan ? findBillingPrice(prices, pendingPlan, selectedCycle) : undefined}
        cycle={selectedCycle}
        nextBillingAt={sub?.currentPeriodEnd ? Number(sub.currentPeriodEnd) : null}
        onConfirm={onConfirmChange}
        onClose={() => {
          setPendingPlan(null);
          setPendingFlow(null);
        }}
        isPending={upgrade.isPending}
      />

      <DowngradeConfirm
        open={pendingFlow === 'downgrade'}
        targetPlan={pendingPlan}
        currentAccounts={accountsQuery.data?.accounts.length ?? 0}
        onConfirm={onConfirmChange}
        onClose={() => {
          setPendingPlan(null);
          setPendingFlow(null);
        }}
        isPending={upgrade.isPending}
      />

      {/* Cancel-paid retention dialog (US-196). */}
      <Dialog
        open={cancelOpen}
        onOpenChange={(next: boolean) => {
          if (!next) setCancelOpen(false);
        }}
      >
        <DialogContent aria-label={t('cancel.title')} data-testid="cancel-subscription-dialog">
          <DialogHeader
            title={t('cancel.retentionTitle')}
            description={t('cancel.retentionBody')}
          />
          <DialogBody>
            <div className="flex flex-col gap-4">
              <Field>
                <Field.Label>{t('cancel.feedbackLabel')}</Field.Label>
                <Field.Control>
                  <Textarea
                    rows={3}
                    placeholder={t('cancel.feedbackPlaceholder')}
                    value={cancelFeedback}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setCancelFeedback(event.target.value)
                    }
                    data-testid="cancel-feedback"
                  />
                </Field.Control>
              </Field>
              <Field>
                <Field.Label>{t('cancel.timingLabel')}</Field.Label>
                <Field.Control>
                  <RadioGroup
                    value={cancelTiming}
                    onValueChange={(next) => setCancelTiming(next === 'now' ? 'now' : 'eop')}
                    orientation="vertical"
                  >
                    <label className="inline-flex items-center gap-2 text-sm text-primary">
                      <Radio value="eop" data-testid="cancel-timing-eop" />
                      <span>{t('cancel.atPeriodEndLabel', { date: periodEndDate })}</span>
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm text-primary">
                      <Radio value="now" data-testid="cancel-timing-now" />
                      <span>{t('cancel.immediateLabel')}</span>
                    </label>
                  </RadioGroup>
                </Field.Control>
              </Field>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setCancelOpen(false)}>
              {t('cancel.keep')}
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={cancel.isPending}
              onClick={onConfirmCancel}
              data-testid="cancel-subscription-confirm"
            >
              {t('cancel.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel-trial dialog (US-195). */}
      <Dialog
        open={cancelTrialOpen}
        onOpenChange={(next: boolean) => {
          if (!next) setCancelTrialOpen(false);
        }}
      >
        <DialogContent aria-label={t('cancelTrial.title')} data-testid="cancel-trial-dialog">
          <DialogHeader
            title={t('cancelTrial.title')}
            description={t('cancelTrial.body', { date: trialEndDate })}
          />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setCancelTrialOpen(false)}>
              {t('cancelTrial.keep')}
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={cancelTrial.isPending}
              onClick={onConfirmCancelTrial}
              data-testid="cancel-trial-confirm"
            >
              {t('cancelTrial.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

interface BillingCycleToggleProps {
  value: BillingCycle;
  onChange: (next: BillingCycle) => void;
}

function BillingCycleToggle({ value, onChange }: BillingCycleToggleProps) {
  const t = useT('billing');
  return (
    <div
      data-component="BillingCycleToggle"
      className="flex items-center justify-center gap-3"
      role="radiogroup"
      aria-label={t('cycle.label')}
    >
      <div className="inline-flex items-center rounded-full border border-default bg-bg-surface p-0.5 text-xs font-semibold">
        <button
          type="button"
          role="radio"
          aria-checked={value === 'monthly'}
          onClick={() => onChange('monthly')}
          data-testid="cycle-monthly"
          className={[
            'rounded-full px-3 py-1 transition-colors',
            value === 'monthly'
              ? 'bg-bg-elevated text-primary shadow-sm'
              : 'text-tertiary hover:text-secondary',
          ].join(' ')}
        >
          {t('cycle.monthly')}
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={value === 'yearly'}
          onClick={() => onChange('yearly')}
          data-testid="cycle-yearly"
          className={[
            'flex items-center gap-1 rounded-full px-3 py-1 transition-colors',
            value === 'yearly'
              ? 'bg-bg-elevated text-primary shadow-sm'
              : 'text-tertiary hover:text-secondary',
          ].join(' ')}
        >
          <span>{t('cycle.yearly')}</span>
        </button>
      </div>
    </div>
  );
}

function PlanSummary({
  data,
  prices,
  locale,
}: {
  data: BillingResponse | undefined;
  prices: readonly BillingPrice[];
  locale: string;
}) {
  const t = useT('billing');
  const sub = data?.subscription;
  const trial = data?.trial;
  const usage = data?.usage;

  if (!sub) {
    return (
      <Banner tone="info" title={t('summary.noSubscription')}>
        {t('summary.noSubHint')}
      </Banner>
    );
  }
  const planName = t(`plans.${sub.plan}.name`);
  const emoji = t(`plans.${sub.plan}.emoji`);
  const price = findBillingPrice(prices, sub.plan, 'monthly');

  let statusLabel = t('summary.statusActive');
  if (sub.status === 'trialing') statusLabel = t('summary.statusTrial');
  else if (sub.status === 'past_due') statusLabel = t('summary.statusPastDue');
  else if (sub.status === 'unpaid') statusLabel = t('summary.statusUnpaid');
  else if (sub.status === 'canceled') statusLabel = t('summary.statusCanceled');

  const subline = computeSubline(sub, trial, t);
  const cancellationDate =
    sub.cancelAtPeriodEnd && sub.currentPeriodEnd
      ? formatDate(Number(sub.currentPeriodEnd), {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : null;

  return (
    <div
      data-component="PlanSummary"
      className="flex items-center gap-3 rounded-xl border border-accent-border bg-bg-elevated px-4 py-3"
    >
      <span className="text-2xl" aria-hidden>
        {emoji}
      </span>
      <div className="flex flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-primary">{planName}</span>
          <Pill tone="accent" variant="soft" size="sm">
            {statusLabel}
          </Pill>
          {cancellationDate ? (
            <Pill
              tone="warning"
              variant="soft"
              size="sm"
              data-testid="plan-summary-cancellation-pill"
            >
              {t('summary.cancellationScheduled', { date: cancellationDate })}
            </Pill>
          ) : null}
          {usage ? (
            <QuotaPill
              resource="aiCalls"
              current={usage.aiCallsUsed}
              limit={usage.aiCallsLimit ?? Infinity}
            />
          ) : null}
        </div>
        <span className="text-xs text-secondary">{subline}</span>
      </div>
      <div className="text-right text-lg font-bold tabular-nums text-primary">
        {t('summary.perMonth', {
          price: price ? formatBillingPrice(price, locale) : '—',
        })}
      </div>
    </div>
  );
}

function computeSubline(
  sub: NonNullable<BillingResponse['subscription']>,
  trial: BillingResponse['trial'] | undefined,
  t: (key: string, params?: Record<string, unknown>) => string,
): string {
  const date = sub.currentPeriodEnd
    ? formatDate(Number(sub.currentPeriodEnd), {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '—';
  if (sub.status === 'trialing') {
    const days = trial?.trialDaysLeft ?? 0;
    return sub.paymentMethodBrand && sub.paymentMethodLast4
      ? t('summary.subTrial', {
          days,
          brand: sub.paymentMethodBrand,
          last4: sub.paymentMethodLast4,
        })
      : t('summary.subTrialNoMethod', { days });
  }
  return sub.paymentMethodBrand && sub.paymentMethodLast4
    ? t('summary.subPaid', {
        date,
        brand: sub.paymentMethodBrand,
        last4: sub.paymentMethodLast4,
      })
    : t('summary.subPaidNoMethod', { date });
}

export { TrialExpiredOverlay };
